import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validate token on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      api.get('/api/users/me')
        .then(userData => {
          setUser(userData);
          return api.get('/api/users/me/subscription').catch(() => null);
        })
        .then(sub => {
          setSubscription(sub);
        })
        .catch(() => {
          api.clearTokens();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    console.log('[Auth] Attempting login...');
    const data = await api.auth.login({ email, password });
    console.log('[Auth] Login response:', data);

    if (data.access_token) {
      console.log('[Auth] Setting tokens...');
      api.setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      console.log('[Auth] User set:', data.user);

      // Fetch subscription
      try {
        console.log('[Auth] Fetching subscription...');
        const sub = await api.get('/api/users/me/subscription');
        console.log('[Auth] Subscription:', sub);
        setSubscription(sub);
      } catch (e) {
        console.log('[Auth] No subscription:', e);
        setSubscription(null);
      }
      return data;
    }
    throw new Error(data.detail || 'خطا در ورود');
  }, []);

  const register = useCallback(async (email, password, fullName) => {
    const data = await api.auth.register({ email, password, full_name: fullName });
    if (data.access_token) {
      api.setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      try {
        const sub = await api.get('/api/users/me/subscription');
        setSubscription(sub);
      } catch (e) {
        setSubscription(null);
      }
      return data;
    }
    throw new Error(data.detail || 'خطا در ثبت نام');
  }, []);

  const logout = useCallback(() => {
    api.clearTokens();
    setUser(null);
    setSubscription(null);
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const sub = await api.get('/api/users/me/subscription');
      setSubscription(sub);
    } catch (e) {
      setSubscription(null);
    }
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
  }, []);

  const value = {
    user,
    subscription,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshSubscription,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
